// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { AddURLLinkParams, urlLinkSchema } from '@/url-link/api/types';
import { z } from 'zod';

// Response schema for the project URL link creation endpoint
const addProjectURLLinkResponseSchema = z
  .object({
    data: urlLinkSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, ...data }));

type AddProjectURLLinkResponse = z.infer<
  typeof addProjectURLLinkResponseSchema
>;

/**
 * Adds a URL link and associates it with a specific project.
 * This function calls the backend endpoint POST /v1/project/{projectKey}/url-link
 * with the URL link details in the request body.
 *
 * @param params - The parameters for creating the project URL link
 * @returns Promise containing the created URL link details
 */
export async function addProjectURLLink(
  params: AddURLLinkParams
): Promise<AddProjectURLLinkResponse> {
  const apiUrl = `/v1/project/${params.entityKey}/url-link`;
  const payload = {
    name: params.name,
    url: params.url,
  };

  const response = await api.post(apiUrl, payload);

  return parseResponse(addProjectURLLinkResponseSchema, response.data, {
    schemaName: 'AddProjectURLLink',
    endpoint: apiUrl,
  });
}
